import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API}/products/${id}`);
        setProduct(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const addToCart = () => {
    const savedCart = localStorage.getItem("nymphea_cart");
    let cart = savedCart ? JSON.parse(savedCart) : [];

    const existing = cart.find((item) => item.product_id === product.id);

    if (existing) {
      cart = cart.map((item) =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      cart.push({ product_id: product.id, quantity, product });
    }

    localStorage.setItem("nymphea_cart", JSON.stringify(cart));
    navigate("/checkout");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Produit non trouvé</p>
          <Link to="/shop">
            <Button className="btn-secondary">Retour à la boutique</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page">
      <section className="pt-32 pb-24 md:pb-32">
        <div className="page-container">
          {/* Back link */}
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Retour à la boutique
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Image */}
            <div className="img-hover-zoom overflow-hidden border border-border/40">
              <img
                src={product.image_url}
                alt={product.nom}
                className="w-full aspect-square object-cover"
              />
            </div>

            {/* Info */}
            <div className="flex flex-col justify-center">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                {product.categorie === "prints" ? "Print" : "Stickers"}
              </p>
              <h1 className="text-3xl md:text-4xl font-heading font-light mb-4">
                {product.nom}
              </h1>
              <p className="text-2xl font-medium mb-6">{product.prix.toFixed(2)}€</p>

              <p className="text-muted-foreground font-light leading-relaxed mb-8">
                {product.description}
              </p>

              {/* Quantity */}
              <div className="flex items-center gap-4 mb-8">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">
                  Quantité
                </span>
                <div className="flex items-center border border-border">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-secondary transition-colors"
                    data-testid="quantity-minus"
                  >
                    <Minus className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <span className="w-12 text-center" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center hover:bg-secondary transition-colors"
                    data-testid="quantity-plus"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {/* Stock */}
              {product.stock > 0 ? (
                <p className="text-sm text-muted-foreground mb-6">
                  {product.stock} en stock
                </p>
              ) : (
                <p className="text-sm text-red-600 mb-6">Rupture de stock</p>
              )}

              {/* Add to cart */}
              <Button
                onClick={addToCart}
                disabled={product.stock === 0}
                className="btn-primary w-full md:w-auto"
                data-testid="add-to-cart-btn"
              >
                Ajouter au panier
              </Button>

              {/* Shipping info */}
              <div className="mt-12 pt-8 border-t border-border/40 space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Livraison</h4>
                  <p className="text-sm text-muted-foreground">
                    Envoi sous 3-5 jours ouvrés. Frais de port offerts à partir de 30€.
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Retrait à l'atelier</h4>
                  <p className="text-sm text-muted-foreground">
                    Gratuit — Récupérez votre commande à La Peauésie.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
